import { useState } from "react";

import {
	api,
	ApiError,
	type MarketplacePluginSkillConfig,
	type McpHttpSkillConfig,
	type McpStdioSkillConfig,
	type PluginSkillConfig,
	type SkillKind,
	type SkillOrigin,
} from "../lib/api.ts";

type Step = "pick-kind" | "fill" | "success";

/**
 * Two-step modal for creating a new skill: pick a kind (MCP HTTP / MCP stdio /
 * Plugin), then fill kind-specific fields. Submit opens a PR against
 * skills-extra.json. Stdio for `external` is disabled — owner-paste of an
 * arbitrary command would be RCE on every host running the agent.
 */
export function NewSkillModal({ onClose }: { onClose: () => void }) {
	const [step, setStep] = useState<Step>("pick-kind");
	const [kind, setKind] = useState<SkillKind>("mcp-http");
	const [origin, setOrigin] = useState<SkillOrigin>("external");
	const [key, setKey] = useState("");
	const [displayName, setDisplayName] = useState("");
	const [description, setDescription] = useState("");

	// mcp-http fields
	const [mcpUrl, setMcpUrl] = useState("");
	const [authKind, setAuthKind] = useState<"none" | "oidc-scope" | "literal-header">("none");
	const [scope, setScope] = useState("");
	const [headerName, setHeaderName] = useState("Authorization");
	const [headerValue, setHeaderValue] = useState("");

	// mcp-stdio fields
	const [stdioCommand, setStdioCommand] = useState("");
	const [stdioArgs, setStdioArgs] = useState("");

	// plugin fields — single SKILL.md textarea for v1.
	const [pluginContent, setPluginContent] = useState(DEFAULT_SKILL_MD);

	// marketplace-plugin fields
	const [marketplaceName, setMarketplaceName] = useState("claude-plugins-official");
	const [pluginName, setPluginName] = useState("");

	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [prUrl, setPrUrl] = useState<string | null>(null);
	const [autoMerged, setAutoMerged] = useState<boolean>(false);

	function suggestKey(name: string) {
		const slug = name
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-|-$/g, "")
			.slice(0, 64);
		if (key === "") setKey(slug);
		setDisplayName(name);
	}

	function buildConfig():
		| McpHttpSkillConfig
		| McpStdioSkillConfig
		| PluginSkillConfig
		| MarketplacePluginSkillConfig
		| null {
		if (kind === "mcp-http") {
			let auth:
				| { kind: "none" }
				| { kind: "oidc-scope"; scope: string }
				| { kind: "literal-header"; header: string; value: string };
			if (authKind === "none") auth = { kind: "none" };
			else if (authKind === "oidc-scope") auth = { kind: "oidc-scope", scope: scope.trim() };
			else auth = { kind: "literal-header", header: headerName.trim(), value: headerValue };
			return { url: mcpUrl.trim(), auth };
		}
		if (kind === "mcp-stdio") {
			const args = stdioArgs
				.split("\n")
				.map((s) => s.trim())
				.filter(Boolean);
			return { command: stdioCommand.trim(), ...(args.length > 0 ? { args } : {}) };
		}
		if (kind === "plugin") {
			return {
				files: [{ path: `skills/${key || "new"}/SKILL.md`, content: pluginContent }],
			};
		}
		if (kind === "marketplace-plugin") {
			return { marketplace: marketplaceName.trim(), name: pluginName.trim() };
		}
		return null;
	}

	async function handleSubmit() {
		setBusy(true);
		setError(null);
		try {
			const config = buildConfig();
			if (!config) {
				setError("invalid config");
				return;
			}
			const result = await api.createSkill({
				key,
				displayName,
				description: description.trim() === "" ? undefined : description,
				origin,
				kind,
				config,
				containsSecret: authKind === "literal-header" && headerValue.length > 0 ? true : undefined,
			});
			setPrUrl(result.htmlUrl);
			setAutoMerged(Boolean(result.merged));
			setStep("success");
		} catch (err) {
			if (err instanceof ApiError) setError(`${err.status} — ${err.message}`);
			else setError((err as Error).message);
		} finally {
			setBusy(false);
		}
	}

	const canSubmit = (() => {
		if (busy) return false;
		if (key.length < 2 || displayName.length === 0) return false;
		if (kind === "mcp-http") {
			if (!mcpUrl.startsWith("http")) return false;
			if (authKind === "oidc-scope" && scope.trim() === "") return false;
			if (authKind === "literal-header" && headerValue.trim() === "") return false;
			return true;
		}
		if (kind === "mcp-stdio") return stdioCommand.trim().length > 0;
		if (kind === "plugin") return pluginContent.trim().length > 0;
		if (kind === "marketplace-plugin") {
			return marketplaceName.trim().length > 0 && pluginName.trim().length > 0;
		}
		return false;
	})();

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
			onClick={onClose}
		>
			<div
				className="w-full max-w-2xl max-h-[90vh] flex flex-col border border-zinc-800 rounded-lg bg-terminal shadow-xl"
				onClick={(e) => e.stopPropagation()}
			>
				<header className="px-4 py-3 border-b border-zinc-800 flex items-baseline justify-between">
					<h2 className="text-base font-semibold">New skill</h2>
					<button
						type="button"
						onClick={onClose}
						className="text-xs text-zinc-500 hover:text-zinc-300"
					>
						close
					</button>
				</header>

				<div className="p-4 space-y-3 overflow-y-auto">
					{step === "pick-kind" && (
						<>
							<div className="text-sm text-zinc-400 mb-2">Choose a kind:</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-2">
								<KindRadio
									label="MCP HTTP"
									description="Remote MCP server reachable over HTTPS. Auth via OIDC scope (our gateway) or literal header (third-party)."
									selected={kind === "mcp-http"}
									onClick={() => setKind("mcp-http")}
								/>
								<KindRadio
									label="MCP stdio"
									description="Local process invoked by the agent (e.g. an mcp-server-* npm package). Only available for built-in skills — external stdio is blocked."
									selected={kind === "mcp-stdio"}
									onClick={() => setKind("mcp-stdio")}
									disabled={origin === "external"}
								/>
								<KindRadio
									label="Plugin (inline SKILL.md)"
									description="Claude Code SKILL.md inlined into our repo. Materialized into a temp plugin-dir per task. Text-only in v1; 64 KB cap."
									selected={kind === "plugin"}
									onClick={() => setKind("plugin")}
								/>
								<KindRadio
									label="Marketplace plugin"
									description="Claude Code plugin from a marketplace (e.g. frontend-design@claude-plugins-official). CLI runs `claude plugin install` on agent startup — idempotent, no manual install per host."
									selected={kind === "marketplace-plugin"}
									onClick={() => setKind("marketplace-plugin")}
								/>
							</div>
							<div className="mt-4 text-sm text-zinc-400">Origin:</div>
							<div className="flex gap-2">
								<OriginButton
									label="External"
									description="Added from this UI."
									selected={origin === "external"}
									onClick={() => setOrigin("external")}
								/>
								<OriginButton
									label="Built-in"
									description="Curated/seeded — usually only edited via direct repo PR. Choose this only if you know what you're doing."
									selected={origin === "builtin"}
									onClick={() => setOrigin("builtin")}
								/>
							</div>
							<div className="flex justify-end mt-4">
								<button
									type="button"
									onClick={() => {
										// reset kind if it's disallowed under chosen origin
										if (kind === "mcp-stdio" && origin === "external") setKind("mcp-http");
										setStep("fill");
									}}
									className="px-3 py-1.5 bg-accent-600 hover:bg-accent-500 text-white text-sm font-medium rounded"
								>
									Next →
								</button>
							</div>
						</>
					)}

					{step === "fill" && (
						<>
							<div>
								<label className="text-xs text-zinc-500">Display name</label>
								<input
									type="text"
									value={displayName}
									onChange={(e) => suggestKey(e.target.value)}
									autoFocus
									placeholder="GitHub MCP"
									className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
								/>
							</div>
							<div>
								<label className="text-xs text-zinc-500">Key (slug)</label>
								<input
									type="text"
									value={key}
									onChange={(e) => setKey(e.target.value.toLowerCase())}
									placeholder="github-mcp"
									className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm font-mono placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
								/>
							</div>
							<div>
								<label className="text-xs text-zinc-500">Description (optional)</label>
								<textarea
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									rows={2}
									className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-200 text-sm resize-y focus:outline-none focus:border-zinc-500"
								/>
							</div>

							{kind === "mcp-http" && (
								<>
									<div>
										<label className="text-xs text-zinc-500">MCP server URL</label>
										<input
											type="text"
											value={mcpUrl}
											onChange={(e) => setMcpUrl(e.target.value)}
											placeholder="https://example.com/mcp"
											className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm font-mono focus:outline-none focus:border-zinc-500"
										/>
									</div>
									<div>
										<label className="text-xs text-zinc-500">Auth</label>
										<div className="flex gap-2 mt-1">
											{(["none", "oidc-scope", "literal-header"] as const).map((k) => (
												<button
													key={k}
													type="button"
													onClick={() => setAuthKind(k)}
													className={`text-xs px-3 py-1 rounded border ${
														authKind === k
															? "border-zinc-400 text-zinc-100"
															: "border-zinc-700 text-zinc-400 hover:border-zinc-500"
													}`}
												>
													{k}
												</button>
											))}
										</div>
									</div>
									{authKind === "oidc-scope" && (
										<div>
											<label className="text-xs text-zinc-500">
												OIDC scope (space-separated tokens; must be in g1-group-agent whitelist)
											</label>
											<input
												type="text"
												value={scope}
												onChange={(e) => setScope(e.target.value)}
												placeholder="openid mcp:tool:graph_query"
												className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm font-mono focus:outline-none focus:border-zinc-500"
											/>
										</div>
									)}
									{authKind === "literal-header" && (
										<>
											<div>
												<label className="text-xs text-zinc-500">Header name</label>
												<input
													type="text"
													value={headerName}
													onChange={(e) => setHeaderName(e.target.value)}
													className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm font-mono focus:outline-none focus:border-zinc-500"
												/>
											</div>
											<div>
												<label className="text-xs text-zinc-500">Header value (stored in repo plaintext)</label>
												<input
													type="text"
													value={headerValue}
													onChange={(e) => setHeaderValue(e.target.value)}
													placeholder="Bearer …"
													className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-amber-200 text-sm font-mono focus:outline-none focus:border-zinc-500"
												/>
												<div className="text-[11px] text-amber-400 mt-1">
													⚠ value will be committed to the (private) repo. Don't use for high-risk secrets.
												</div>
											</div>
										</>
									)}
								</>
							)}

							{kind === "mcp-stdio" && (
								<>
									<div>
										<label className="text-xs text-zinc-500">Command</label>
										<input
											type="text"
											value={stdioCommand}
											onChange={(e) => setStdioCommand(e.target.value)}
											placeholder="npx"
											className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm font-mono focus:outline-none focus:border-zinc-500"
										/>
									</div>
									<div>
										<label className="text-xs text-zinc-500">Args (one per line)</label>
										<textarea
											value={stdioArgs}
											onChange={(e) => setStdioArgs(e.target.value)}
											rows={3}
											className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm font-mono resize-y focus:outline-none focus:border-zinc-500"
										/>
									</div>
								</>
							)}

							{kind === "plugin" && (
								<div>
									<label className="text-xs text-zinc-500">SKILL.md</label>
									<textarea
										value={pluginContent}
										onChange={(e) => setPluginContent(e.target.value)}
										rows={14}
										className="mt-1 w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-200 text-sm font-mono resize-y focus:outline-none focus:border-zinc-500"
									/>
									<div className="text-[11px] text-zinc-600 mt-1">
										v1: text-only, single SKILL.md; 64 KB cap. Multi-file/binary support — v2.
									</div>
								</div>
							)}

							{kind === "marketplace-plugin" && (
								<>
									<div>
										<label className="text-xs text-zinc-500">Marketplace</label>
										<input
											type="text"
											value={marketplaceName}
											onChange={(e) => setMarketplaceName(e.target.value)}
											placeholder="claude-plugins-official"
											className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm font-mono placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
										/>
									</div>
									<div>
										<label className="text-xs text-zinc-500">Plugin name</label>
										<input
											type="text"
											value={pluginName}
											onChange={(e) => setPluginName(e.target.value)}
											placeholder="frontend-design"
											className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm font-mono placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
										/>
										<div className="text-[11px] text-zinc-600 mt-1">
											CLI will run <code>claude plugin install {pluginName || "<name>"}@
											{marketplaceName || "<marketplace>"}</code> on every host on agent startup.
											Idempotent. Plugin must be discoverable by the host's <code>claude</code>{" "}
											CLI.
										</div>
									</div>
								</>
							)}

							{error && <div className="text-xs text-red-300">{error}</div>}
						</>
					)}

					{step === "success" && (
						<div className="text-sm text-emerald-300 space-y-2">
							<div>
								{autoMerged
									? "✓ Auto-merged. Skill goes live with the next auto-deploy (~1 min)."
									: "✓ PR opened — auto-merge didn't take, finish it manually."}
							</div>
							{prUrl && (
								<a href={prUrl} target="_blank" rel="noreferrer" className="underline break-all">
									{prUrl}
								</a>
							)}
						</div>
					)}
				</div>

				<footer className="px-4 py-3 border-t border-zinc-800 flex justify-end gap-2">
					{step === "fill" && (
						<>
							<button
								type="button"
								onClick={() => setStep("pick-kind")}
								disabled={busy}
								className="px-3 py-1.5 border border-zinc-700 hover:border-zinc-600 text-zinc-300 text-sm rounded"
							>
								← Back
							</button>
							<button
								type="button"
								onClick={handleSubmit}
								disabled={!canSubmit}
								className="px-3 py-1.5 bg-accent-600 hover:bg-accent-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-sm font-medium rounded"
							>
								{busy ? "Opening PR…" : "Open PR"}
							</button>
						</>
					)}
					{step === "success" && (
						<button
							type="button"
							onClick={onClose}
							className="px-3 py-1.5 bg-accent-600 hover:bg-accent-500 text-white text-sm font-medium rounded"
						>
							Done
						</button>
					)}
				</footer>
			</div>
		</div>
	);
}

function KindRadio({
	label,
	description,
	selected,
	onClick,
	disabled,
}: {
	label: string;
	description: string;
	selected: boolean;
	onClick: () => void;
	disabled?: boolean;
}) {
	return (
		<button
			type="button"
			disabled={disabled}
			onClick={onClick}
			title={disabled ? "External + stdio is blocked for security reasons." : undefined}
			className={`text-left p-3 border rounded transition-colors ${
				disabled
					? "border-zinc-900 bg-terminal text-zinc-700 cursor-not-allowed"
					: selected
						? "border-zinc-400 bg-zinc-800/40 text-zinc-100"
						: "border-zinc-700 hover:border-zinc-500 text-zinc-300"
			}`}
		>
			<div className="text-sm font-medium">{label}</div>
			<div className="text-[11px] text-zinc-500 mt-1">{description}</div>
		</button>
	);
}

function OriginButton({
	label,
	description,
	selected,
	onClick,
}: {
	label: string;
	description: string;
	selected: boolean;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			title={description}
			className={`text-xs px-3 py-1.5 rounded border ${
				selected
					? "border-zinc-400 text-zinc-100 bg-zinc-800/40"
					: "border-zinc-700 text-zinc-400 hover:border-zinc-500"
			}`}
		>
			{label}
		</button>
	);
}

const DEFAULT_SKILL_MD = `# Skill name

What this skill does and when the agent should invoke it.

## Example
Optional: a sample task this skill helps with.
`;
